import classNames from "classnames";
import React, {
    ReactNode,
    useEffect,
    useState,
} from "react";
import DefaultAvatar from "../../assets/img/logo-reverse-big.svg";
import "./Avatar.css";
import { useTheme } from "../../contexts/Theme";

export type AvatarVariant = "xxs" | "xs" | "sm" | "sm-2" | "sm-3" | "md" | "lg" | "lg-v3" | "xl";
export interface AvatarProps {
    src: string | undefined;
    variant?:AvatarVariant;
    className?: string;
    isCircle?: boolean;
    defaultIcon?: ReactNode;
    statusIcon?: ReactNode;
    alt?: string;
    avatarStatusColor?: "success" | "danger" | "empty";
    shouldShowLoading?: boolean;
}

export default function Avatar({
    src, variant, className, isCircle, defaultIcon,
    statusIcon, avatarStatusColor, alt, shouldShowLoading,
}: AvatarProps): ReactNode {
    const [image, setImage] = useState(src);
    const [shouldShowDefault, setShouldShowDefault] = useState(false);
    const theme = useTheme();
    useEffect(() => {
        setShouldShowDefault(src == null);
        setImage(src);
    }, [src]);

    const handleError = () => {
        setShouldShowDefault(true);
        setImage(DefaultAvatar);
    };
    if (shouldShowDefault && defaultIcon) {
        return defaultIcon;
    }

    let bottomRightIcon = null;
    if (avatarStatusColor) {
        bottomRightIcon = (
            <span className={classNames("avatar-status", `avatar-${avatarStatusColor}`)}>
                <span className="avatar-status-icon" />
            </span>
        );
    } else if (statusIcon) {
        bottomRightIcon = (
            <span className="avatar-status">{statusIcon}</span>
        );
    }

    let roundedClass;
    if (isCircle) {
        roundedClass = "rounded-circle";
    } else if (!theme.isV3) {
        roundedClass = "rounded";
    } else {
        roundedClass = "";
    }
    if (bottomRightIcon) {
        return (
            <span className={classNames("avatar", className)}>
                {bottomRightIcon}
                <img
                    alt={alt}
                    src={image ?? DefaultAvatar}
                    onError={handleError}
                    className={`avatar avatar-${variant} ${className} ${roundedClass}`}
                />
            </span>
        );
    }

    return (
        <>
            <img
                alt="User profile"
                src={image ?? DefaultAvatar}
                onError={handleError}
                className={`avatar avatar-${variant} ${className} ${roundedClass}`}
            />
            {shouldShowLoading && (
                <span className={`loading-icon loading-icon-${variant}`} />
            )}
        </>

    );
}

Avatar.defaultProps = {
    variant: "md",
    className: "me-2",
    isCircle: true,
    defaultIcon: undefined,
    statusIcon: undefined,
    avatarStatusColor: undefined,
    alt: "User Profile image",
    shouldShowLoading: true,
};
